import type {
  CartaoCnpjExtractionPreview,
  EstablishmentCnae,
  EstablishmentDetailResponse,
  EstablishmentProfileRequest,
} from "@/lib/adminEstablishmentsApi";

/** Um campo extraído do cartão CNPJ confrontado com o valor atual do cadastro. */
export interface ExtractionField {
  /** Chave do campo no perfil (campo de PUT /profile). */
  key: keyof EstablishmentProfileRequest;
  /** Rótulo legível (pt-BR). */
  label: string;
  /** Valor hoje no cadastro (normalizado para exibição). */
  current: string;
  /** Valor sugerido pela extração. */
  extracted: string;
  /** `true` quando o valor extraído difere do atual (sobrescreveria algo). */
  differs: boolean;
}

/** Linha de CNAE extraída, para revisão. */
export interface ExtractionCnaeRow {
  code: string;
  description: string;
  primary: boolean;
}

/** Resultado da comparação: campos escalares diffáveis + CNAEs extraídos (lista inteira substitui). */
export interface ExtractionReview {
  fields: ExtractionField[];
  cnaes: ExtractionCnaeRow[];
  cnaesDiffer: boolean;
}

const norm = (v: string | null | undefined): string => (v ?? "").trim();

/**
 * Mapeia os campos escalares do cartão CNPJ para chaves do perfil. CNPJ é PROPOSITALMENTE omitido — a troca
 * de CNPJ nunca passa por aqui (vira sucessão). `name` do cadastro = razão social.
 */
const FIELD_MAP: { key: keyof EstablishmentProfileRequest; label: string; from: keyof CartaoCnpjExtractionPreview }[] = [
  { key: "name", label: "Razão social", from: "name" },
  { key: "tradeName", label: "Nome fantasia", from: "tradeName" },
  { key: "stateRegistration", label: "Inscrição estadual", from: "stateRegistration" },
  { key: "addressZipCode", label: "CEP", from: "addressZipCode" },
  { key: "addressStreet", label: "Logradouro", from: "addressStreet" },
  { key: "addressNumber", label: "Número", from: "addressNumber" },
  { key: "addressComplement", label: "Complemento", from: "addressComplement" },
  { key: "addressDistrict", label: "Bairro", from: "addressDistrict" },
  { key: "addressCity", label: "Município", from: "addressCity" },
  { key: "addressState", label: "UF", from: "addressState" },
  { key: "email", label: "E-mail", from: "email" },
  { key: "phone", label: "Telefone", from: "phone" },
];

/**
 * Constrói a revisão lado-a-lado entre o cadastro atual e a extração. Só entram na revisão os campos em que a
 * extração trouxe um valor NÃO vazio (não há como "apagar" com a extração). Cada campo marca se difere do atual
 * para a UI poder destacar o que seria sobrescrito.
 */
export function buildExtractionReview(
  current: EstablishmentDetailResponse,
  preview: CartaoCnpjExtractionPreview,
): ExtractionReview {
  const fields: ExtractionField[] = [];
  for (const { key, label, from } of FIELD_MAP) {
    const extracted = norm(preview[from] as string | null | undefined);
    if (!extracted) continue;
    const cur = norm((current as Record<string, unknown>)[key] as string | null | undefined);
    fields.push({ key, label, current: cur, extracted, differs: cur !== extracted });
  }

  const cnaes: ExtractionCnaeRow[] = [];
  if (preview.cnaePrincipal) {
    cnaes.push({ code: preview.cnaePrincipal.code, description: preview.cnaePrincipal.description, primary: true });
  }
  for (const c of preview.cnaesSecundarios ?? []) {
    cnaes.push({ code: c.code, description: c.description, primary: false });
  }

  const currentCnaeKey = [...current.cnaes]
    .map((c) => `${c.code}:${c.primary ? "P" : "S"}`)
    .sort()
    .join("|");
  const extractedCnaeKey = [...cnaes]
    .map((c) => `${c.code}:${c.primary ? "P" : "S"}`)
    .sort()
    .join("|");
  const cnaesDiffer = cnaes.length > 0 && currentCnaeKey !== extractedCnaeKey;

  return { fields, cnaes, cnaesDiffer };
}

/**
 * Monta o corpo do PUT /profile aplicando SOMENTE os campos confirmados pelo usuário sobre o estado atual.
 * Campos não marcados preservam o valor atual (nunca sobrescreve sem confirmação). Se `applyCnaes` for true, a
 * lista inteira de CNAEs extraídos substitui a atual; senão a atual é preservada.
 */
export function buildProfileFromReview(
  current: EstablishmentDetailResponse,
  review: ExtractionReview,
  confirmedKeys: ReadonlySet<string>,
  applyCnaes: boolean,
): EstablishmentProfileRequest {
  const body: EstablishmentProfileRequest = {
    name: current.name,
    tradeName: current.tradeName,
    stateRegistration: current.stateRegistration,
    municipalRegistration: current.municipalRegistration,
    email: current.email,
    phone: current.phone,
    whatsapp: current.whatsapp,
    website: current.website,
    addressZipCode: current.addressZipCode,
    addressStreet: current.addressStreet,
    addressNumber: current.addressNumber,
    addressComplement: current.addressComplement,
    addressDistrict: current.addressDistrict,
    addressCity: current.addressCity,
    addressState: current.addressState,
    addressCountry: current.addressCountry,
    cnaes: current.cnaes,
  };

  for (const field of review.fields) {
    if (confirmedKeys.has(String(field.key))) {
      (body as Record<string, unknown>)[field.key] = field.extracted;
    }
  }

  if (applyCnaes) {
    const cnaes: EstablishmentCnae[] = review.cnaes.map((c) => ({
      code: c.code,
      description: c.description,
      primary: c.primary,
    }));
    body.cnaes = cnaes;
  }

  return body;
}
